/**
 * The sign-in page's one question: the work e-mail. Continue → the BFF decides the method. Passkeys need no
 * button press to be found: the browser lists them in this field's autofill (conditional UI), armed for as long as
 * the step is shown. A quiet "Sign in with a passkey" remains for browsers without autofill, and for people who
 * reach for it out of habit.
 */
import { Icon, Spinner } from '@evt/flow-stream-ui'
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser'
import { type SubmitEvent, useEffect, useRef, useState } from 'react'
import { loginOptions } from '../../api/auth'
import {
  cancelPasskeyCeremony,
  runAutofillAuthentication,
  supportsPasskeyAutofill,
  supportsPasskeys
} from '../../auth/passkey'
import { isEmail } from '../../auth/sign-in-flow'
import { useI18n } from '../../i18n'

/** The authentication challenge cookie lives 5 minutes; autofill is re-armed with a fresh one before it lapses. */
const AUTOFILL_REARM_MS = 4 * 60_000

export function IdentifyStep({
  email,
  onEmailChange,
  busy,
  onContinue,
  onPasskey,
  onAutofill,
  orgSso,
  legacySso
}: {
  email: string
  onEmailChange: (email: string) => void
  busy: boolean
  onContinue: (email: string) => void
  onPasskey: () => Promise<void>
  /** The person picked a passkey from the e-mail field's suggestions (its own failures are the caller's to show). */
  onAutofill: (resp: AuthenticationResponseJSON) => Promise<void>
  /** `/login?sso=<slug>`: an organization's own link leads with its SSO. */
  orgSso?: () => void
  /** The shared (legacy) OIDC provider, when one is configured. */
  legacySso?: { label: string; start: () => void }
}) {
  const { t } = useI18n()
  const [invalid, setInvalid] = useState(false)
  const [armKey, setArmKey] = useState(0)
  const passkeys = supportsPasskeys()
  // Latest callbacks for the long-lived autofill ceremony, which must not restart on every render.
  const handlers = useRef({ onAutofill })
  handlers.current = { onAutofill }

  useEffect(() => {
    // `armKey` re-arms after a modal passkey attempt, which aborted the autofill one.
    void armKey
    let stopped = false
    let rearm: ReturnType<typeof setTimeout> | undefined
    const arm = async () => {
      if (stopped || !(await supportsPasskeyAutofill())) return
      try {
        const options = await loginOptions()
        if (stopped) return
        rearm = setTimeout(() => {
          cancelPasskeyCeremony()
          void arm()
        }, AUTOFILL_REARM_MS)
        let resp: AuthenticationResponseJSON
        try {
          resp = await runAutofillAuthentication(options)
        } finally {
          clearTimeout(rearm)
        }
        if (!stopped) await handlers.current.onAutofill(resp)
      } catch {
        // Autofill is passive: the person asked for nothing, so its own failures (a browser that refuses the
        // background request, an aborted or expired one) are never shown. A passkey they did pick reports
        // through `onAutofill`, which shows its own errors.
      }
    }
    void arm()
    return () => {
      stopped = true
      clearTimeout(rearm)
      cancelPasskeyCeremony()
    }
  }, [armKey])

  const submit = (e: SubmitEvent) => {
    e.preventDefault()
    const value = email.trim()
    if (!isEmail(value)) {
      setInvalid(true)
      return
    }
    cancelPasskeyCeremony()
    onContinue(value)
  }

  return (
    <div className="login-body">
      {orgSso && (
        <>
          <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={orgSso}>
            <Icon name="globe" size={15} /> {t.login.continueWithOrgSso}
          </button>
          <div className="login-divider">
            <span>{t.common.or}</span>
          </div>
        </>
      )}
      <form className="login-form" onSubmit={submit} noValidate>
        <label className="field-label" htmlFor="signin-email">
          {t.login.workEmail}
        </label>
        <input
          id="signin-email"
          name="email"
          className="login-input"
          type="email"
          inputMode="email"
          // `webauthn` last: the browser offers this site's passkeys in the suggestions.
          autoComplete="username webauthn"
          autoCapitalize="none"
          spellCheck={false}
          // biome-ignore lint/a11y/noAutofocus: the page asks exactly one thing; focusing it is the point.
          autoFocus
          required
          placeholder={t.login.workEmailPlaceholder}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? 'signin-email-error' : undefined}
          value={email}
          onChange={(e) => {
            onEmailChange(e.target.value)
            setInvalid(false)
          }}
        />
        {invalid && (
          <span id="signin-email-error" className="login-field-error" role="alert">
            {t.login.invalidEmail}
          </span>
        )}
        <button type="submit" className={`btn btn-block${orgSso ? '' : ' btn-primary'}`} disabled={busy}>
          {busy ? <Spinner size={14} /> : null} {t.common.continue}
        </button>
      </form>

      {(passkeys || legacySso) && (
        <div className="login-alt">
          {passkeys && (
            <button
              type="button"
              className="btn btn-ghost btn-block"
              disabled={busy}
              onClick={() => {
                void onPasskey().finally(() => setArmKey((k) => k + 1))
              }}
            >
              <Icon name="shield" size={14} /> {t.login.usePasskey}
            </button>
          )}
          {legacySso && (
            <button type="button" className="btn btn-ghost btn-block" disabled={busy} onClick={legacySso.start}>
              <MicrosoftLogo /> {t.login.signInWith(legacySso.label)}
            </button>
          )}
        </div>
      )}

      <p className="login-hint">{t.login.firstTimeHint}</p>
    </div>
  )
}

/** Microsoft four-squares mark (brand guidelines: usable on custom sign-in buttons). */
function MicrosoftLogo() {
  return (
    <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}
